import React from 'react';
import { Switch, Route, NavLink, Redirect } from 'react-router-dom';
import { logout } from '../Auth';
import EntityLevelsScreen from './screens/EntityLevelsScreen';
import AlgorithmMappingScreen from './screens/AlgorithmMappingScreen';
import AliasesScreen from './screens/AliasesScreen';
import CustomAlgorithmsScreen from './screens/CustomAlgorithmsScreen';
import CustomDetectorsScreen from './screens/CustomDetectorsScreen';
import SettingsScreen from './screens/SettingsScreen';
import LandscapeSeverityScreen from './screens/LandscapeSeverityScreen';
import HealthScreen from './screens/HealthScreen';
import './AdvancedConfig.css';

// Self-contained shell for the hidden Advanced Settings app. Deliberately imports NONE of the
// main app's Navigation — it renders its own header + tab strip so it looks like a separate app.
// Reached only via the URL /advanced-config (no link points here from the main app).

const TABS = [
  { path: '/advanced-config/entities', label: 'Entity Levels' },
  { path: '/advanced-config/algorithms', label: 'Algorithm Mapping' },
  { path: '/advanced-config/aliases', label: 'Algorithm Aliases' },
  { path: '/advanced-config/custom-algorithms', label: 'Custom Algorithms' },
  { path: '/advanced-config/custom-detectors', label: 'Custom Detectors' },
  { path: '/advanced-config/landscape-severity', label: 'Landscape Severity' },
  { path: '/advanced-config/settings', label: 'Settings' },
  { path: '/advanced-config/health', label: 'Health & Errors' },
];

const AdvancedConfigApp = ({ isAdmin, config, user }) => {
  // Product version from the deployment config (same source AdminPanel/LicensePanel use).
  const version = config && config.VERSION ? String(config.VERSION).replace(/^v/, '') : null;

  const handleLogout = async () => {
    await logout();
    // See Navigation/Components.js — replace() so the operator console is not the entry the
    // browser's Back button restores (C2D-1198).
    window.location.replace('/');
  };

  return (
    <div className='adv-config-root' data-testid='adv-config-root'>
      <header className='adv-config-header'>
        <div className='adv-config-brand'>
          <span className='adv-config-brand-mark'>C2</span>
          <span className='adv-config-brand-title'>Advanced Settings</span>
          <span className='adv-config-brand-badge'>Operator Console</span>
          {version && <span className='adv-config-brand-version'>v{version}</span>}
        </div>
        <button type='button' className='adv-config-logout' onClick={handleLogout}>
          Logout
        </button>
      </header>

      <div className='adv-config-body'>
        <nav className='adv-config-nav'>
          {TABS.map((t) => (
            <NavLink key={t.path} to={t.path} className='adv-config-nav-item'
              activeClassName='adv-config-nav-item-active'>
              {t.label}
            </NavLink>
          ))}
        </nav>

        <main className='adv-config-main'>
          {!isAdmin && (
            <div className='adv-config-warning'>
              You are signed in without an admin role. You can view this console, but saving
              changes requires an administrator account.
            </div>
          )}
          <Switch>
            <Route path='/advanced-config/entities'>
              <EntityLevelsScreen canEdit={isAdmin} />
            </Route>
            <Route path='/advanced-config/algorithms'>
              <AlgorithmMappingScreen canEdit={isAdmin} />
            </Route>
            <Route path='/advanced-config/aliases'>
              <AliasesScreen canEdit={isAdmin} />
            </Route>
            <Route path='/advanced-config/custom-algorithms'>
              <CustomAlgorithmsScreen canEdit={isAdmin} />
            </Route>
            <Route path='/advanced-config/custom-detectors'>
              <CustomDetectorsScreen canEdit={isAdmin} />
            </Route>
            <Route path='/advanced-config/landscape-severity'>
              <LandscapeSeverityScreen canEdit={isAdmin} />
            </Route>
            <Route path='/advanced-config/settings'>
              <SettingsScreen canEdit={isAdmin} user={user} />
            </Route>
            <Route path='/advanced-config/health'>
              <HealthScreen />
            </Route>
            <Redirect exact from='/advanced-config/lookup-lists' to='/advanced-config/aliases' />
            <Route path='/advanced-config'>
              <Redirect to='/advanced-config/entities' />
            </Route>
          </Switch>
        </main>
      </div>
    </div>
  );
};

export default AdvancedConfigApp;
